/**
 * TAP Athletes Portal — bootstrap + router.
 *
 * Routes:
 *   #/                  → home (athletes list + upload + recent)
 *   #/athletes/:id      → deep-dive (header card + trend chart)
 */

import { supabase } from './db/supabase.js';
import {
  getAthletesWithStats,
  getRecentUploads,
  getAthleteById,
  getPitchesByAthlete,
  getPocketRadarThrowsByAthlete,
  getRapsodoUploadsByAthlete,
  getPocketRadarUploadsByAthlete,
} from './db/queries.js';
import { renderSessionSurface } from './ui/session-surface.js';
import { rapsodoSurface } from './features/rapsodo/surface.js';
import { pocketRadarSurface } from './features/pocket-radar/surface.js';
import { parseRoute, navigate, onRouteChange } from './router.js';
import { onAuthChange, getSession, renderLogin, signOut } from './auth/session.js';
import './ui/components/ta-upload.js';
import './ui/components/ta-bulk-import.js';
import './ui/components/ta-add-pitcher.js';
import './ui/components/ta-edit-pitcher.js';
import './ui/components/ta-session-picker.js';
import './ui/components/ta-sessions-table.js';
// <ta-sessions-chart> is dynamic-imported on the athlete deep-dive page only
// (Chart.js dependency).
// <ta-trend> is dynamic-imported on the athlete deep-dive page only.
// Keeps Chart.js (~70% of the bundle) out of the home-page first paint.

class AppRoot extends HTMLElement {
  connectedCallback() {
    // Auth gate. Nothing renders until an approved coach is signed in.
    // onAuthChange fires immediately with the initial session, then on
    // every sign-in / sign-out. The explicit getSession() is a fallback so
    // the login screen always paints on first load, never a blank page.
    onAuthChange((session) => this.applyAuth(session));
    getSession().then((session) => {
      if (!session && !this._appStarted) renderLogin(this);
    });
  }

  applyAuth(session) {
    if (session) {
      if (this._appStarted) return; // ignore token refreshes once running
      this._appStarted = true;
      this.startApp();
    } else {
      this._appStarted = false;
      renderLogin(this);
    }
  }

  startApp() {
    this.renderShell();
    this.querySelector('#signout-btn')?.addEventListener('click', () => signOut());

    onRouteChange((route) => this.handleRoute(route));

    // Athlete row clicks → navigate. Event delegation, attached to the host.
    this.addEventListener('click', (e) => {
      const row = e.target.closest('.athletes-table__row');
      if (row) {
        navigate(`/athletes/${row.dataset.athleteId}`);
      }
    });

    // After any upload OR bulk import, refresh the data WITHOUT tearing down
    // the upload UI — that summary state with per-file results needs to stay
    // visible. On home, just re-fetch the data sections; on athlete pages,
    // a full re-render is fine since uploads aren't usually done from there.
    const refreshAfterChange = () => {
      const route = parseRoute();
      if (route.name === 'home' && this.querySelector('#view-root ta-upload, #view-root ta-bulk-import')) {
        this.loadAthletes();
        this.loadRecentUploads();
        this.checkDb();
      } else {
        this.handleRoute(route);
      }
    };
    this.addEventListener('upload-complete', refreshAfterChange);
    this.addEventListener('athletes-imported', refreshAfterChange);
    // After an athlete edit on the Profile tab, re-render the current
    // route so the header card and table values pick up the new fields.
    this.addEventListener('athlete-updated', () => this.handleRoute(parseRoute()));
  }

  renderShell() {
    this.innerHTML = /* html */ `
      <header class="app-header">
        <div class="container app-header__inner">
          <a href="#/" class="app-header__logo-link">
            <span class="app-header__logo">TAP Athletes</span>
            <span class="app-header__badge">Portal</span>
          </a>
          <nav class="app-header__nav">
            <a href="#/">Athletes</a>
            <button type="button" class="app-header__signout" id="signout-btn">Sign out</button>
          </nav>
        </div>
      </header>

      <main class="container app-main" id="view-root"></main>

      <footer class="app-footer">
        <div class="container app-footer__inner">
          <span>TAP Athletes — internal use only.</span>
          <span class="db-status" data-state="loading">
            <span class="db-status__dot"></span>
            <span class="db-status__msg">Checking DB…</span>
          </span>
          <span class="app-footer__build">Build ${import.meta.env.MODE}</span>
        </div>
      </footer>
    `;
    this.checkDb();
  }

  async handleRoute(route) {
    const root = this.querySelector('#view-root');
    if (!root) return;

    if (route.name === 'home') {
      this.renderHome(root);
    } else if (route.name === 'athlete') {
      await this.renderAthlete(root, route.params.id, route.params.tab);
    } else {
      root.innerHTML = `
        <section class="page-section">
          <h2 class="page-section__title">Not found</h2>
          <p>No route matches <code>${escapeHtml(route.attempted ?? location.hash)}</code>.</p>
          <p><a href="#/">← Back to athletes</a></p>
        </section>
      `;
    }

    // Update window title for clarity in browser tabs / history.
    document.title = route.name === 'athlete'
      ? 'Athlete · TAP Portal'
      : 'TAP Athletes Portal';
  }

  // ----------------------------------------------------------
  // Home view
  // ----------------------------------------------------------
  renderHome(root) {
    root.innerHTML = `
      <section class="page-section">
        <p class="eyebrow">Upload</p>
        <ta-upload></ta-upload>
      </section>

      <section class="page-section">
        <header class="page-section__header page-section__header--row">
          <div>
            <h2 class="page-section__title">Athletes</h2>
            <p class="page-section__sub" id="athletes-count">Loading…</p>
          </div>
          <div class="page-section__actions">
            <ta-add-pitcher></ta-add-pitcher>
            <ta-bulk-import></ta-bulk-import>
          </div>
        </header>
        <div id="athletes-table"></div>
      </section>

      <section class="page-section">
        <header class="page-section__header">
          <h2 class="page-section__title">Recent uploads</h2>
        </header>
        <div id="recent-uploads"></div>
      </section>
    `;
    this.loadAthletes();
    this.loadRecentUploads();
    this.checkDb();
  }

  async loadAthletes() {
    const tableEl = this.querySelector('#athletes-table');
    const countEl = this.querySelector('#athletes-count');
    if (!tableEl || !countEl) return;
    try {
      const athletes = await getAthletesWithStats();
      countEl.textContent = `${athletes.length} active athlete${athletes.length === 1 ? '' : 's'}`;

      if (athletes.length === 0) {
        tableEl.innerHTML = `
          <div class="empty-state">
            <p>No athletes yet. Drop a Rapsodo CSV above to add the pitcher.</p>
          </div>
        `;
        return;
      }

      const sorted = [...athletes].sort((a, b) => {
        if (!a.last_training_date && !b.last_training_date) return 0;
        if (!a.last_training_date) return 1;
        if (!b.last_training_date) return -1;
        return b.last_training_date.localeCompare(a.last_training_date);
      });

      tableEl.innerHTML = `
        <table class="athletes-table">
          <thead>
            <tr>
              <th>Name</th><th>Age</th><th>Playing</th><th>Throws</th>
              <th>Last training</th><th>Sessions</th><th>Pitches</th>
              <th>Peak velo</th><th>Primary</th><th>Intent</th>
            </tr>
          </thead>
          <tbody>${sorted.map(renderAthleteRow).join('')}</tbody>
        </table>
      `;
    } catch (err) {
      tableEl.innerHTML = `<div class="error-state">Couldn't load athletes: ${escapeHtml(err.message ?? err)}</div>`;
    }
  }

  async loadRecentUploads() {
    const el = this.querySelector('#recent-uploads');
    if (!el) return;
    try {
      const uploads = await getRecentUploads(5);
      if (uploads.length === 0) {
        el.innerHTML = `<div class="empty-state empty-state--small">No uploads yet.</div>`;
        return;
      }
      el.innerHTML = `
        <ul class="upload-list">
          ${uploads.map((u) => {
            const athleteName = u.athletes
              ? `${u.athletes.first_name} ${u.athletes.last_name}`
              : 'Unknown athlete';
            return `
              <li class="upload-list__item">
                <div class="upload-list__main">
                  <span class="upload-list__athlete">${escapeHtml(athleteName)}</span>
                  <span class="upload-list__meta">${u.row_count} pitches</span>
                </div>
                <span class="upload-list__date">${formatDateCT(u.uploaded_at)}</span>
              </li>
            `;
          }).join('')}
        </ul>
      `;
    } catch (err) {
      el.innerHTML = `<div class="error-state">Couldn't load recent uploads: ${escapeHtml(err.message ?? err)}</div>`;
    }
  }

  // ----------------------------------------------------------
  // Athlete deep-dive view (tabbed: Dashboards | Files | Notes | Profile)
  // ----------------------------------------------------------
  async renderAthlete(root, id, tab = 'dashboards') {
    root.innerHTML = `
      <section class="page-section">
        <p class="eyebrow"><a href="#/">← Athletes</a></p>
        <div id="athlete-header">Loading…</div>
      </section>

      <nav class="athlete-tabs" role="tablist">
        <a href="#/athletes/${id}/dashboards" role="tab"
           class="athlete-tabs__tab ${tab === 'dashboards' ? 'is-active' : ''}">Dashboards</a>
        <a href="#/athletes/${id}/files" role="tab"
           class="athlete-tabs__tab ${tab === 'files' ? 'is-active' : ''}">Files</a>
        <a href="#/athletes/${id}/notes" role="tab"
           class="athlete-tabs__tab ${tab === 'notes' ? 'is-active' : ''}">Notes</a>
        <a href="#/athletes/${id}/profile" role="tab"
           class="athlete-tabs__tab ${tab === 'profile' ? 'is-active' : ''}">Profile</a>
      </nav>

      <div id="athlete-tab-content" class="athlete-tab-content"></div>
    `;

    const headerEl = this.querySelector('#athlete-header');
    const tabContentEl = this.querySelector('#athlete-tab-content');

    let athlete;
    try {
      athlete = await getAthleteById(id);
    } catch (err) {
      headerEl.innerHTML = `<div class="error-state">Couldn't load athlete: ${escapeHtml(err.message ?? err)}</div>`;
      return;
    }

    if (!athlete) {
      headerEl.innerHTML = `<div class="error-state">Athlete not found.</div>`;
      return;
    }

    headerEl.innerHTML = renderAthleteHeader(athlete);

    if (tab === 'files') {
      await this.renderFilesTab(tabContentEl, athlete);
    } else if (tab === 'notes') {
      this.renderNotesTab(tabContentEl, athlete);
    } else if (tab === 'profile') {
      this.renderProfileTab(tabContentEl, athlete);
    } else {
      await this.renderDashboardsTab(tabContentEl, athlete);
    }
  }

  // ---- Tab: Dashboards — table-first BI-style layout ----
  // Top: sessions table (one row per session, sortable). Below: simple
  // categorical "Across sessions" trend chart. Per-pitch scatter +
  // heat map are loaded lazily as a "Session detail" drill-down,
  // shown only when a session row is clicked or the picker is in
  // Single mode.
  async renderDashboardsTab(root, athlete) {
    root.innerHTML = `
      <section class="page-section" id="throws-timeline-section" hidden>
        <header class="page-section__header">
          <h2 class="page-section__title">Throws — unified timeline</h2>
          <p class="page-section__sub">
            Every throw across all instruments on one chart. The default metric
            is velocity (visible for both Rapsodo and Pocket Radar throws); other
            metrics naturally show only the throws that have that data.
          </p>
        </header>
        <ta-throws-timeline id="athlete-throws-timeline"></ta-throws-timeline>
      </section>

      <section class="page-section" id="rapsodo-section" hidden>
        <header class="page-section__header page-section__header--row">
          <div>
            <h2 class="page-section__title">Rapsodo — sessions</h2>
            <p class="page-section__sub">
              One row per bullpen / live AB. Click any row to drill into that session's per-pitch detail.
            </p>
          </div>
        </header>
        <ta-sessions-table id="rapsodo-sessions-table" kind="rapsodo"></ta-sessions-table>
        <div id="rapsodo-sessions-chart-wrap" class="across-sessions"></div>
        <div id="rapsodo-detail" class="session-detail" hidden>
          <header class="page-section__header page-section__header--row">
            <div>
              <h3 class="page-section__title page-section__title--sm">Session detail</h3>
              <p class="page-section__sub" id="rapsodo-detail-sub"></p>
            </div>
            <div class="page-section__actions">
              <ta-session-picker id="rapsodo-session-picker"></ta-session-picker>
              <button type="button" class="compare-toggle" id="compare-toggle" data-active="false">
                <span class="compare-toggle__icon">+</span>
                <span class="compare-toggle__label">Compare to previous period</span>
              </button>
            </div>
          </header>
          <div id="athlete-trend"></div>
          <div id="athlete-heatmap"></div>
        </div>
      </section>

      <section class="page-section" id="pr-section" hidden>
        <header class="page-section__header page-section__header--row">
          <div>
            <h2 class="page-section__title">Pocket Radar — sessions</h2>
            <p class="page-section__sub">
              One row per long-toss / drill / lesson session. Click a row to drill into per-throw detail.
            </p>
          </div>
        </header>
        <ta-sessions-table id="pr-sessions-table" kind="pocket-radar"></ta-sessions-table>
        <div id="pr-sessions-chart-wrap" class="across-sessions"></div>
        <div id="pr-detail" class="session-detail" hidden>
          <header class="page-section__header page-section__header--row">
            <div>
              <h3 class="page-section__title page-section__title--sm">Session detail</h3>
              <p class="page-section__sub" id="pr-detail-sub"></p>
            </div>
            <div class="page-section__actions">
              <ta-session-picker id="pr-session-picker"></ta-session-picker>
            </div>
          </header>
          <div id="athlete-pr-trend"></div>
        </div>
      </section>

      <section class="page-section" id="no-data-section" hidden>
        <div class="empty-state">No data uploaded yet for this athlete.</div>
      </section>
    `;

    try {
      const [pitches, prThrows, rapsodoSessions, prSessions] = await Promise.all([
        getPitchesByAthlete(athlete.id),
        getPocketRadarThrowsByAthlete(athlete.id),
        getRapsodoUploadsByAthlete(athlete.id),
        getPocketRadarUploadsByAthlete(athlete.id),
      ]);

      const hasRapsodo = pitches.length > 0;
      const hasPocketRadar = prThrows.length > 0;

      if (!hasRapsodo && !hasPocketRadar) {
        root.querySelector('#no-data-section').hidden = false;
        return;
      }

      // Lazy-load chart-heavy components only if there's data to render.
      // ta-throws-timeline loads whenever either source has data — it's
      // the tool-agnostic top-line view.
      const importPromises = [
        import('./ui/components/ta-throws-timeline.js'),
      ];
      if (hasRapsodo) {
        importPromises.push(
          import('./ui/components/ta-sessions-chart.js'),
          import('./ui/components/ta-trend.js'),
          import('./ui/components/ta-heatmap.js'),
        );
      }
      if (hasPocketRadar) {
        importPromises.push(
          import('./ui/components/ta-sessions-chart.js'),
          import('./ui/components/ta-pr-trend.js'),
        );
      }
      await Promise.all(importPromises);

      // Unified timeline at the top — tool-agnostic, every data point.
      root.querySelector('#throws-timeline-section').hidden = false;
      const timeline = root.querySelector('#athlete-throws-timeline');
      timeline.pitches = pitches;
      timeline.throws = prThrows;

      const onReRender = () => this.handleRoute(parseRoute());
      if (hasRapsodo) {
        renderSessionSurface(root, {
          descriptor: rapsodoSurface,
          rows: pitches,
          sessions: rapsodoSessions,
          onReRender,
        });
      }
      if (hasPocketRadar) {
        renderSessionSurface(root, {
          descriptor: pocketRadarSurface,
          rows: prThrows,
          sessions: prSessions,
          onReRender,
        });
      }
    } catch (err) {
      root.innerHTML = `<div class="error-state">Couldn't load dashboards: ${escapeHtml(err.message ?? err)}</div>`;
    }
  }

  // ---- Tab: Files ----
  async renderFilesTab(root, athlete) {
    root.innerHTML = `
      <section class="page-section">
        <header class="page-section__header">
          <h2 class="page-section__title">Files</h2>
          <p class="page-section__sub">
            Every artifact attached to this athlete — Rapsodo CSVs and PDFs,
            Pocket Radar exports, paper assessment scans, photos, video, notes —
            grouped by training day.
          </p>
        </header>
        <div id="athlete-files"></div>
      </section>
    `;
    await import('./ui/components/ta-files-tab.js');
    const filesEl = root.querySelector('#athlete-files');
    filesEl.innerHTML = `<ta-files-tab></ta-files-tab>`;
    const filesComponent = filesEl.querySelector('ta-files-tab');
    filesComponent.athleteId = athlete.id;
  }

  // ---- Tab: Notes (stub for now) ----
  renderNotesTab(root, _athlete) {
    root.innerHTML = `
      <section class="page-section">
        <header class="page-section__header">
          <h2 class="page-section__title">Notes</h2>
        </header>
        <div class="empty-state">
          <p>Notes coming in a future update.</p>
          <p class="empty-state__sub">
            Will support per-athlete coach notes, threaded discussion entries,
            and dated training reflections — modeled on TRAQ's notes flow.
          </p>
        </div>
      </section>
    `;
  }

  // ---- Tab: Profile (read-only summary; edit deferred) ----
  renderProfileTab(root, athlete) {
    const fullName = athlete.nickname
      ? `${athlete.first_name} "${athlete.nickname}" ${athlete.last_name}`
      : `${athlete.first_name} ${athlete.last_name}`;
    root.innerHTML = `
      <section class="page-section">
        <header class="page-section__header page-section__header--row">
          <div>
            <h2 class="page-section__title">Profile</h2>
            <p class="page-section__sub">Click Edit to change name, ages, throws, or nickname.</p>
          </div>
          <ta-edit-pitcher></ta-edit-pitcher>
        </header>
        <dl class="profile-list">
          <div class="profile-list__row">
            <dt>Full name</dt><dd>${escapeHtml(fullName)}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Athlete age</dt>
            <dd>${athlete.athlete_age != null ? `${athlete.athlete_age} years` : '—'}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Playing age</dt>
            <dd>${athlete.playing_age ? escapeHtml(athlete.playing_age) : '—'}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Throws</dt><dd>${athlete.throws_hand ?? '—'}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Joined</dt><dd>${athlete.joined_date ? formatDate(athlete.joined_date) : '—'}</dd>
          </div>
          ${athlete.dob ? `
            <div class="profile-list__row">
              <dt>Date of birth</dt><dd>${formatDate(athlete.dob)}</dd>
            </div>
          ` : ''}
          <div class="profile-list__row">
            <dt>Last training</dt><dd>${athlete.last_training_date ? formatDateCT(athlete.last_training_date) : '—'}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Sessions</dt><dd>${athlete.total_uploads ?? 0}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Pitches</dt><dd>${athlete.total_pitches ?? 0}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Peak velocity</dt>
            <dd>${athlete.peak_velo_mph != null ? athlete.peak_velo_mph.toFixed(1) + ' mph' : '—'}</dd>
          </div>
          <div class="profile-list__row">
            <dt>Status</dt>
            <dd>${athlete.is_active ? 'Active' : 'Inactive'}</dd>
          </div>
        </dl>
      </section>
    `;
    const editEl = root.querySelector('ta-edit-pitcher');
    if (editEl) editEl.athlete = athlete;
  }

  async checkDb() {
    const status = this.querySelector('.db-status');
    const msg = this.querySelector('.db-status__msg');
    if (!status || !msg) return;
    try {
      const [athletes, pitches] = await Promise.all([
        supabase.from('athletes').select('id', { count: 'exact', head: true }),
        supabase.from('rapsodo_pitches').select('id', { count: 'exact', head: true }),
      ]);
      if (athletes.error) throw athletes.error;
      if (pitches.error) throw pitches.error;
      status.dataset.state = 'ok';
      msg.textContent = `${athletes.count} athletes · ${pitches.count} pitches`;
    } catch (err) {
      status.dataset.state = 'error';
      msg.textContent = err.message ?? String(err);
    }
  }
}

// ----------------------------------------------------------
// Render helpers
// ----------------------------------------------------------

function renderAthleteRow(a) {
  const fullName = a.nickname
    ? `${a.first_name} "${a.nickname}" ${a.last_name}`
    : `${a.first_name} ${a.last_name}`;
  return `
    <tr class="athletes-table__row" data-athlete-id="${a.id}">
      <td class="athletes-table__name">
        ${escapeHtml(fullName)}
      </td>
      <td>${a.athlete_age ?? '—'}</td>
      <td>${a.playing_age ? escapeHtml(a.playing_age) : '—'}</td>
      <td>${a.throws_hand ?? '—'}</td>
      <td>${a.last_training_date ? formatDateCT(a.last_training_date) : '—'}</td>
      <td>${a.total_uploads}</td>
      <td>${a.total_pitches}</td>
      <td>${a.peak_velo_mph != null ? a.peak_velo_mph.toFixed(1) + ' mph' : '—'}</td>
      <td>${pitchBadge(a.primary_pitch_type)}</td>
      <td>${a.intent_summary ? intentLabel(a.intent_summary) : '—'}</td>
    </tr>
  `;
}

function renderAthleteHeader(a) {
  const fullName = a.nickname
    ? `${a.first_name} "${a.nickname}" ${a.last_name}`
    : `${a.first_name} ${a.last_name}`;
  const ageBits = [];
  if (a.athlete_age != null) ageBits.push(`Age ${a.athlete_age}`);
  if (a.playing_age) ageBits.push(`Plays ${escapeHtml(a.playing_age)}`);
  const ageLabel = ageBits.length ? ageBits.join(' · ') : 'Age not set';
  return `
    <div class="athlete-header">
      <div class="athlete-header__main">
        <h1 class="athlete-header__name">
          ${escapeHtml(fullName)}
        </h1>
        <p class="athlete-header__sub">
          ${ageLabel}
          · Throws ${a.throws_hand ?? '?'}
          · Joined ${a.joined_date ? formatDate(a.joined_date) : '?'}
        </p>
      </div>
      <ul class="athlete-header__stats">
        <li><span class="stat__label">Last training</span><span class="stat__value">${a.last_training_date ? formatDateCT(a.last_training_date) : '—'}</span></li>
        <li><span class="stat__label">Sessions</span><span class="stat__value">${a.total_uploads}</span></li>
        <li><span class="stat__label">Pitches</span><span class="stat__value">${a.total_pitches}</span></li>
        <li><span class="stat__label">Peak velo</span><span class="stat__value">${a.peak_velo_mph != null ? a.peak_velo_mph.toFixed(1) + ' mph' : '—'}</span></li>
      </ul>
    </div>
  `;
}

function pitchBadge(type) {
  if (!type) return '—';
  const cls = `pitch-badge pitch-badge--${type.toLowerCase()}`;
  return `<span class="${cls}">${escapeHtml(type)}</span>`;
}

function intentLabel(intent) {
  return { high_intent: 'High', low_intent: 'Low', mixed: 'Mixed' }[intent] ?? intent;
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function formatDateCT(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// Date-only fields (dob, joined_date) — render the literal date with no
// timezone conversion. Without this, a date like '2024-01-01' parses as
// UTC midnight and displays "Dec 31, 2023, 6:00 PM" in CT.
function formatDate(isoDate) {
  if (!isoDate) return '—';
  const datePart = String(isoDate).split('T')[0];
  return new Date(datePart + 'T00:00:00Z').toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

customElements.define('app-root', AppRoot);
